import { useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface SortState<K extends string> {
  by: K;
  dir: SortDirection;
}

export interface SortableColumn<T, K extends string> {
  key: K;
  /** Pull the value used for ordering. Falsy values sink to the end. */
  accessor: (row: T) => string | number | boolean | null | undefined;
}

interface UseTableStateOptions<T, K extends string> {
  rows: T[];
  columns: ReadonlyArray<SortableColumn<T, K>>;
  /** Initial column key sorted on, plus direction. Leave undefined to ship the rows un-sorted. */
  initialSort?: SortState<K> | undefined;
  /** Initial page size — defaults to 25, the sweet spot for studio tables. */
  initialPageSize?: number;
}

interface UseTableStateResult<T, K extends string> {
  /** Slice of `rows` visible on the current page, sorted per `sort`. */
  page: T[];
  /** Sorted full list — useful when the parent needs the full set (e.g. for export). */
  sorted: T[];
  /** Total row count after sorting (== rows.length unless filtered externally). */
  total: number;
  /** 0-based page index. */
  pageIndex: number;
  pageSize: number;
  pageCount: number;
  setPageIndex: (next: number) => void;
  setPageSize: (next: number) => void;
  /** Toggle the sort: same key flips direction, new key resets to ascending. */
  toggleSort: (key: K) => void;
  sort: SortState<K> | null;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export const TABLE_PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS;

/**
 * Tiny client-side table state — wraps sort + pagination in a single hook so
 * the studio's small-to-medium tables (≤ a few thousand rows) get a uniform
 * UX without dragging in a full data-grid dependency.
 *
 * Use `<SortableHeader>` for the column headers and `<TablePager>` for the
 * footer; both read the state this hook returns.
 */
export function useTableState<T, K extends string>({
  rows,
  columns,
  initialSort,
  initialPageSize = 25,
}: UseTableStateOptions<T, K>): UseTableStateResult<T, K> {
  const [sort, setSort] = useState<SortState<K> | null>(initialSort ?? null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSizeState] = useState(initialPageSize);

  const accessors = useMemo(() => {
    const m = new Map<K, SortableColumn<T, K>['accessor']>();
    for (const c of columns) m.set(c.key, c.accessor);
    return m;
  }, [columns]);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const accessor = accessors.get(sort.by);
    if (!accessor) return rows;
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      // Falsy values (null / undefined / '' / 0 unless it's a real measure) sink
      // to the bottom regardless of direction so the user can spot incomplete
      // rows at a glance.
      const aMissing = av === null || av === undefined || av === '';
      const bMissing = bv === null || bv === undefined || bv === '';
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor;
      const as = String(av);
      const bs = String(bv);
      return as.localeCompare(bs, undefined, { numeric: true }) * factor;
    });
  }, [rows, sort, accessors]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePageIndex = Math.min(pageIndex, pageCount - 1);
  const page = useMemo(
    () => sorted.slice(safePageIndex * pageSize, (safePageIndex + 1) * pageSize),
    [sorted, safePageIndex, pageSize],
  );

  return {
    page,
    sorted,
    total: sorted.length,
    pageIndex: safePageIndex,
    pageSize,
    pageCount,
    setPageIndex,
    setPageSize: (n: number) => {
      setPageSizeState(n);
      setPageIndex(0);
    },
    toggleSort: (key: K) =>
      setSort((prev) =>
        prev && prev.by === key
          ? { by: key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
          : { by: key, dir: 'asc' },
      ),
    sort,
  };
}
